import React from 'react';
import { Course, UserRole } from '../../types/lms.types';

interface AssessmentsViewProps {
  course: Course;
  userRole: UserRole;
  selectedModule: string;
}

const AssessmentsView: React.FC<AssessmentsViewProps> = ({ 
  course, 
  userRole, 
  selectedModule 
}) => {
  // Mock assessments data combining assignments and quizzes
  const assessments = [
    {
      id: 1,
      type: 'assignment',
      title: 'Linear Algebra Problem Set',
      description: 'Complete exercises 1-15 from Chapter 3. Show all working steps.',
      moduleId: 'mod1',
      dueDate: '20/1/2024',
      status: 'Pending',
      submissions: '32/45',
      maxMarks: 100,
      icon: '📝'
    },
    {
      id: 2,
      type: 'quiz',
      title: 'Algebra Fundamentals Quiz',
      description: 'Test your understanding of basic algebra concepts.',
      moduleId: 'mod1',
      dueDate: '18/1/2024',
      status: 'Available',
      maxMarks: 50,
      timeLimit: '30 minutes',
      icon: '📋'
    },
    {
      id: 3,
      type: 'assignment',
      title: 'Video Presentation - Mathematical Concepts',
      description: 'Create a 5-minute video explaining a mathematical concept of your choice.',
      moduleId: 'mod2',
      dueDate: '15/1/2024',
      status: 'Submitted',
      submissions: '45/45',
      maxMarks: 50,
      icon: '🎥'
    },
    {
      id: 4,
      type: 'quiz',
      title: 'Linear Equations Assessment',
      description: 'Comprehensive quiz on linear equations and systems.',
      moduleId: 'mod2',
      dueDate: '22/1/2024',
      status: 'Available',
      maxMarks: 75,
      timeLimit: '45 minutes',
      icon: '📋'
    }
  ].filter(assessment => selectedModule === 'all' || assessment.moduleId === selectedModule);

  const getStatusBadge = (status: string) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    
    switch (status) {
      case 'Pending':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'Submitted':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'Available':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'Graded':
        return `${baseClasses} bg-purple-100 text-purple-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getActionButton = (assessment: any) => {
    if (userRole === 'student') {
      switch (assessment.status) {
        case 'Available':
          return assessment.type === 'quiz' ? 'Start Quiz' : 'Submit Assignment';
        case 'Submitted':
          return 'View Submission';
        case 'Graded':
          return 'View Grade';
        default:
          return 'View Details';
      }
    } else {
      return assessment.type === 'quiz' ? 'Manage Quiz' : 'View Submissions';
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <h2 className="text-xl font-semibold">Assessments</h2>
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <span>{assessments.filter(a => a.type === 'assignment').length} Assignments</span>
          <span>{assessments.filter(a => a.type === 'quiz').length} Quizzes</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {assessments.map((assessment) => (
          <div
            key={`${assessment.type}-${assessment.id}`}
            className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow"
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{assessment.icon}</span>
                  <div>
                    <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                      {assessment.type}
                    </span>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      {assessment.title}
                    </h3>
                  </div>
                </div>
                <span className={getStatusBadge(assessment.status)}>
                  {assessment.status}
                </span>
              </div>

              <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">
                {assessment.description}
              </p>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Due Date:</span>
                  <span className="font-medium">{assessment.dueDate}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Max Marks:</span>
                  <span className="font-medium">{assessment.maxMarks}</span>
                </div>
                {assessment.submissions && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Submissions:</span>
                    <span className="font-medium">{assessment.submissions}</span>
                  </div>
                )}
                {assessment.timeLimit && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Time Limit:</span>
                    <span className="font-medium">{assessment.timeLimit}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <button className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  {getActionButton(assessment)}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AssessmentsView;
